import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, Pressable, TextInput, StyleSheet, ScrollView, Platform } from 'react-native';
import { EvilIcons, AntDesign } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import uniqueId from 'lodash/uniqueId';
import { SizableText } from 'tamagui';

interface TagInputProps {
  placeHolder: string,
  tags: string[],
  setTags: (tags: string[]) => void,
  enabled?: boolean,
  initialTags?: string[],
  disable?: boolean,
  label?: string,
  disableInput?: boolean
}

const toCapitalCase = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const TagComponent = React.memo(({ text, cullTagFromTags, disableRemove }: { text: string, cullTagFromTags: (text: string) => void, disableRemove?: boolean }) => {
  const minWidth = text.length * 10;
  const theme = themeSelector();
  return (
    <View style={[styles.tagContainer, { backgroundColor: theme ? "#ececec" : "#333333" }]}>
      <SizableText numberOfLines={1} style={[styles.tagText, { minWidth, color: theme ? "#333333" : "white" }]}>{text}</SizableText>
      {!disableRemove && (
        <Pressable onPress={() => cullTagFromTags(text)} style={styles.deleteButton}>
          <EvilIcons name="trash" size={20} color={theme ? "#666666" : "white"} />
        </Pressable>
      )}
    </View>
  );
});

const TagInput: React.FC<TagInputProps> = ({ disable, label, initialTags = [], disableInput, placeHolder, tags, setTags, enabled = true }) => {
  const inputRef = useRef<TextInput>(null);
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const theme = themeSelector();

  const inputValueChangeHandler = useCallback((inputChange: string) => {
    if (inputChange.endsWith(' ')) {
      const words = inputChange.trim().split(' ').filter(Boolean);
      if (words.length > 0) {
        // Capitalize and filter out duplicates
        const newTags = words
          .map(toCapitalCase)
          .filter(tag => tag && !tags.includes(tag));
        if (newTags.length > 0) {
          setTags([...tags, ...newTags]);
        }
        setInputValue('');
        return;
      }
    }
    setInputValue(inputChange);
  }, [tags, setTags]);

  const cullTagFromTags = useCallback((tag: string) => {
    setTags(tags.filter(element => element !== tag));
  }, [tags, setTags]);

  return (
    <View style={{ flex: 1, alignSelf: "stretch", minHeight: 80, width: '100%' }}>
      {
        !disableInput &&
      <TextInput
        editable={enabled}
        ref={inputRef}
        value={inputValue}
        onChangeText={inputValueChangeHandler}
        onBlur={() => setIsFocused(false)}
        onFocus={() => setIsFocused(true)}
        placeholder={placeHolder || "Tags, separated by spaces"}
        placeholderTextColor={theme ? '#64748b' : '#94a3b8'}
        style={{
          flex: 1,
          alignSelf: "stretch",
          minHeight: 40,
          backgroundColor: 'transparent',
          borderWidth: 0,
          color: theme ? '#1e293b' : '#f8fafc',
          fontSize: 16,
          paddingHorizontal: 0,
          paddingVertical: 0,
          textAlign: 'left',
        }}
      />
      }
      {tags?.length > 0 && (
        <View style={[styles.tagsContainer]}>
          <View style={[styles.scrollContainer, { gap: 10 }]}>
            <Animatable.View useNativeDriver animation='fadeInLeft' style={[styles.iconContainer, { minWidth: 60 }]}>
              <AntDesign name="tags" size={36} color="rgb(90,192,234)" />
            </Animatable.View>
            <ScrollView
              style={{ flex: 1, maxWidth: '100%' }}
              showsHorizontalScrollIndicator
              horizontal
              contentContainerStyle={[styles.scrollContent]}>
              {tags.map((tag, index) => (
                <TagComponent
                  disableRemove={disable}
                  text={toCapitalCase(tag)}
                  cullTagFromTags={cullTagFromTags}
                  key={uniqueId(tag + index)}
                />
              ))}
            </ScrollView>
          </View>
      </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    zIndex: 2,
    minHeight: 120,
    maxWidth: 350,
    marginBottom: 30,
  },
  inputOutline: {
    flex: 1,
    borderRadius: 5,
    minWidth: '90%',
    padding: 10,
  },
  tagsContainer: {
    flex: 5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 20,
    minHeight: 50,
    maxHeight: 55,
    left: -20
  },
  scrollContainer: {
    flex: 1,
    gap: 20,
    flexDirection: 'row',
    alignItems: 'center',
    maxHeight: 55,
    minHeight: 50,
  },
  iconContainer: {
    flex: 1,
    maxWidth: 35,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollContent: {
    gap: 10,
    alignItems: 'center',
  },
  tagContainer: {
    gap: 8,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'grey',
    borderRadius: 20,
    flexDirection: 'row',
    maxHeight: 40,
    minHeight: 36,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginHorizontal: 5,
  },
  tagText: {
    fontSize: 14,
    color: "white",
    fontWeight: '500',
    textAlign: "center",
    lineHeight: 20,
  },
  deleteButton: {
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 4,
    padding: 2,
  },
});

export default TagInput;
